import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { notificationQueryKeys } from '@/hooks/queries/notification.queries';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { getApiErrorMessage } from '@/lib/api/error';
import {
  UserNotificationPreferencesService,
  userNotificationPreferencesQueryKey,
  type UserNotificationPreferences,
  type UserNotificationPreferencesUpdate,
} from '@/services/user-notification-preferences.service';
import type { User } from '@/types/domain/user';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, BellRing, Loader2, MessageCircleMore, MessageSquareText, Monitor } from 'lucide-react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';

type PreferenceKey = keyof UserNotificationPreferences;

interface NotificationChannelRowProps {
  checked: boolean;
  description: string;
  disabled?: boolean;
  icon: ReactNode;
  id: string;
  onCheckedChange: (checked: boolean) => void;
  title: string;
}

function NotificationChannelRow({
  checked,
  description,
  disabled,
  icon,
  id,
  onCheckedChange,
  title,
}: NotificationChannelRowProps) {
  return (
    <div className="border-border flex items-start justify-between gap-4 rounded-2xl border p-4">
      <div className="flex min-w-0 gap-3">
        <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          {icon}
        </span>
        <div>
          <label htmlFor={id} className="text-foreground cursor-pointer text-sm font-semibold">
            {title}
          </label>
          <p className="text-muted-foreground mt-1 text-sm leading-5">{description}</p>
        </div>
      </div>

      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={`focus-visible:ring-ring mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
          checked ? 'bg-primary' : 'bg-input'
        }`}
      >
        <span
          aria-hidden="true"
          className={`bg-background pointer-events-none block h-5 w-5 rounded-full shadow-lg transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

export function NotificationPreferencesPanel({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const { state: pushState, enablePush, disablePush } = usePushNotifications();
  const preferencesQuery = useQuery({
    queryKey: userNotificationPreferencesQueryKey,
    queryFn: UserNotificationPreferencesService.get,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: PreferenceKey; value: boolean }) => {
      if (key === 'pushEnabled' && value) {
        const enabled = await enablePush();

        if (!enabled) {
          throw new Error('Autorize as notificações do navegador para ativar este canal.');
        }
      }

      const updated = await UserNotificationPreferencesService.update({
        [key]: value,
      } as UserNotificationPreferencesUpdate);

      if (key === 'pushEnabled' && !value) {
        try {
          await disablePush();
        } catch {
          toast.warning('Preferência salva', {
            description: 'O Push foi bloqueado no servidor, mas não foi possível remover este dispositivo.',
          });
        }
      }

      return updated;
    },
    onSuccess: (preferences, { key }) => {
      queryClient.setQueryData(userNotificationPreferencesQueryKey, preferences);

      if (key === 'webappEnabled') {
        void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.user(user.id) });
      }

      toast.success('Preferências de notificação atualizadas.');
    },
    onError: (error) => {
      toast.error('Não foi possível atualizar as notificações', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    },
  });

  const preferences = preferencesQuery.data;
  const isBusy = preferencesQuery.isLoading || updateMutation.isPending;
  const update = (key: PreferenceKey) => (value: boolean) => updateMutation.mutate({ key, value });
  const whatsappAvailable = Boolean(user.isPhoneVerified && user.isPhoneWhatsapp);
  const smsAvailable = Boolean(user.isPhoneVerified);

  return (
    <Card className="border-border">
      <CardHeader className="space-y-2">
        <CardTitle>Notificações</CardTitle>
        <CardDescription>
          Escolha onde quer receber os avisos da sua conta. Códigos de acesso e mensagens essenciais não são afetados.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {preferencesQuery.isError ? (
          <div className="border-destructive/30 bg-destructive/5 rounded-2xl border p-4">
            <p className="text-destructive text-sm">Não foi possível carregar suas preferências.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => void preferencesQuery.refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <NotificationChannelRow
              id="notification-webapp"
              icon={<Monitor className="h-5 w-5" />}
              title="Central do Vira Tempo"
              description="Mostra os avisos no sino e na central de notificações do app."
              checked={preferences?.webappEnabled ?? true}
              disabled={isBusy}
              onCheckedChange={update('webappEnabled')}
            />
            <NotificationChannelRow
              id="notification-push"
              icon={pushState === 'denied' ? <BellOff className="h-5 w-5" /> : <BellRing className="h-5 w-5" />}
              title="Notificações em tempo real"
              description={
                pushState === 'denied'
                  ? 'Bloqueado pelo navegador. Libere a permissão nas configurações do site.'
                  : pushState === 'unsupported'
                    ? 'Este navegador não oferece suporte a notificações em tempo real.'
                    : 'Receba avisos mesmo quando o app estiver em segundo plano.'
              }
              checked={preferences?.pushEnabled ?? true}
              disabled={isBusy}
              onCheckedChange={update('pushEnabled')}
            />
            <NotificationChannelRow
              id="notification-whatsapp"
              icon={<MessageCircleMore className="h-5 w-5" />}
              title="WhatsApp"
              description={
                whatsappAvailable
                  ? `Enviar para ${user.phoneNumber}.`
                  : 'Entre com um número verificado pelo WhatsApp para liberar este canal.'
              }
              checked={preferences?.whatsappEnabled ?? false}
              disabled={isBusy || !whatsappAvailable}
              onCheckedChange={update('whatsappEnabled')}
            />
            <NotificationChannelRow
              id="notification-sms"
              icon={<MessageSquareText className="h-5 w-5" />}
              title="SMS"
              description={
                smsAvailable ? `Enviar para ${user.phoneNumber}.` : 'Verifique seu telefone para liberar este canal.'
              }
              checked={preferences?.smsEnabled ?? false}
              disabled={isBusy || !smsAvailable}
              onCheckedChange={update('smsEnabled')}
            />
          </>
        )}

        {isBusy ? (
          <p className="text-muted-foreground flex items-center gap-2 pt-1 text-xs" aria-live="polite">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Salvando preferências…
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
